import type { ManifestMedia } from '@signage/shared';
import type { ClockAdapter, FileSystemAdapter, LoggerAdapter } from './adapters.js';
import type { PlayerApiClient } from './api-client.js';

export interface DownloadResult {
  media: ManifestMedia;
  path: string;
  bytesDownloaded: number;
  resumed: boolean;
}

export interface DownloadFailure {
  media: ManifestMedia;
  message: string;
  attempts: number;
}

export interface DownloadProgress {
  mediaId: string;
  receivedBytes: number;
  totalBytes: number | null;
}

export interface DownloadManagerOptions {
  cacheDir: string;
  maxAttempts?: number;
  /** Base delay for exponential backoff, in milliseconds. */
  backoffBaseMs?: number;
  maxBackoffMs?: number;
  concurrency?: number;
  onProgress?: (progress: DownloadProgress) => void;
  /** Return true to abort the run (e.g. the manifest changed again). */
  shouldCancel?: () => boolean;
}

/**
 * Downloads media files safely.
 *
 * Every file is written to `<sha256>.download`, resumed with a Range request when the server
 * supports it, verified against the manifest checksum, and only then renamed to its final
 * name. A partially downloaded or corrupt file therefore can never become active content —
 * the rename is the commit point.
 */
export class DownloadManager {
  private readonly maxAttempts: number;
  private readonly backoffBaseMs: number;
  private readonly maxBackoffMs: number;
  private readonly concurrency: number;

  constructor(
    private readonly fs: FileSystemAdapter,
    private readonly api: PlayerApiClient,
    private readonly clock: ClockAdapter,
    private readonly logger: LoggerAdapter,
    private readonly options: DownloadManagerOptions,
  ) {
    this.maxAttempts = options.maxAttempts ?? 5;
    this.backoffBaseMs = options.backoffBaseMs ?? 1000;
    this.maxBackoffMs = options.maxBackoffMs ?? 60_000;
    this.concurrency = Math.max(1, options.concurrency ?? 2);
  }

  finalPath(sha256: string): string {
    return this.fs.join(this.options.cacheDir, sha256);
  }

  private tempPath(sha256: string): string {
    return this.fs.join(this.options.cacheDir, `${sha256}.download`);
  }

  /** Downloads the given files, running up to `concurrency` at a time. */
  async downloadAll(items: ManifestMedia[]): Promise<{
    completed: DownloadResult[];
    failed: DownloadFailure[];
    cancelled: boolean;
  }> {
    await this.fs.ensureDir(this.options.cacheDir);
    const queue = [...items];
    const completed: DownloadResult[] = [];
    const failed: DownloadFailure[] = [];
    let cancelled = false;

    const worker = async (): Promise<void> => {
      for (;;) {
        if (this.options.shouldCancel?.()) {
          cancelled = true;
          return;
        }
        const item = queue.shift();
        if (!item) return;
        try {
          completed.push(await this.downloadOne(item));
        } catch (err) {
          const failure = err as { message?: string; attempts?: number; cancelled?: boolean };
          if (failure.cancelled) {
            cancelled = true;
            return;
          }
          failed.push({
            media: item,
            message: failure.message ?? 'Download failed',
            attempts: failure.attempts ?? this.maxAttempts,
          });
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(this.concurrency, Math.max(queue.length, 1)) }, worker),
    );

    return { completed, failed, cancelled };
  }

  /**
   * Downloads one file with retry, resume and verification.
   * Returns immediately if a verified copy already exists.
   */
  async downloadOne(media: ManifestMedia): Promise<DownloadResult> {
    const finalPath = this.finalPath(media.sha256);
    const tempPath = this.tempPath(media.sha256);

    const existing = await this.fs.stat(finalPath);
    if (existing && existing.size === media.sizeBytes) {
      return { media, path: finalPath, bytesDownloaded: 0, resumed: false };
    }
    if (existing) {
      // Wrong size: the file is not what the manifest describes, so start again.
      this.logger.warn('Cached file has an unexpected size, re-downloading', {
        mediaId: media.mediaId,
        expected: media.sizeBytes,
        actual: existing.size,
      });
      await this.fs.remove(finalPath);
    }

    let lastError = 'Download failed';
    let resumed = false;

    for (let attempt = 1; attempt <= this.maxAttempts; attempt += 1) {
      if (this.options.shouldCancel?.()) {
        throw Object.assign(new Error('Cancelled'), { cancelled: true });
      }
      try {
        const partial = await this.fs.stat(tempPath);
        let startByte = partial?.size ?? 0;
        if (startByte > media.sizeBytes) {
          // A stale or corrupt partial file; discard it rather than resume past the end.
          await this.fs.remove(tempPath);
          startByte = 0;
        }

        const response = await this.api.downloadRange(media.url, startByte);

        if (response.status === 416) {
          // The range is unsatisfiable: the partial file is bad. Restart from zero.
          await this.fs.remove(tempPath);
          throw new Error('Server rejected the resume range');
        }
        if (!response.ok) {
          throw new Error(`Server responded ${response.status}`);
        }
        if (!response.body) {
          throw new Error('Empty response body');
        }

        if (startByte > 0 && !response.supportsResume) {
          // The server ignored the Range header and is sending the whole file again.
          await this.fs.remove(tempPath);
          startByte = 0;
        } else if (startByte > 0) {
          resumed = true;
        }

        let received = startByte;
        const onProgress = this.options.onProgress;
        const total = response.totalBytes ?? media.sizeBytes;
        const source = onProgress
          ? (async function* (body: AsyncIterable<Uint8Array>) {
              for await (const chunk of body) {
                received += chunk.length;
                onProgress({ mediaId: media.mediaId, receivedBytes: received, totalBytes: total });
                yield chunk;
              }
            })(response.body)
          : response.body;

        await this.fs.appendStream(tempPath, source);

        const written = await this.fs.stat(tempPath);
        if (!written || written.size !== media.sizeBytes) {
          throw new Error(
            `Size mismatch: expected ${media.sizeBytes} bytes, got ${written?.size ?? 0}`,
          );
        }

        const hash = await this.fs.hashFile(tempPath);
        if (hash !== media.sha256) {
          // A corrupt download must never be published; drop it and try again.
          await this.fs.remove(tempPath);
          throw new Error('Checksum mismatch');
        }

        // Commit point: the file becomes visible under its final name atomically.
        await this.fs.rename(tempPath, finalPath);

        return {
          media,
          path: finalPath,
          bytesDownloaded: media.sizeBytes - startByte,
          resumed,
        };
      } catch (err) {
        if ((err as { cancelled?: boolean }).cancelled) throw err;
        lastError = (err as Error).message ?? 'Download failed';
        this.logger.warn('Download attempt failed', {
          mediaId: media.mediaId,
          attempt,
          error: lastError,
        });
        if (attempt < this.maxAttempts) {
          const delay = Math.min(
            this.maxBackoffMs,
            this.backoffBaseMs * 2 ** (attempt - 1) + Math.floor(Math.random() * 250),
          );
          await this.clock.sleep(delay);
        }
      }
    }

    throw Object.assign(new Error(lastError), { attempts: this.maxAttempts });
  }

  /** Removes leftover `.download` files that no longer correspond to a wanted file. */
  async cleanupPartials(wantedHashes: Set<string>): Promise<number> {
    let removed = 0;
    let names: string[];
    try {
      names = await this.fs.listDir(this.options.cacheDir);
    } catch {
      return 0;
    }
    for (const name of names) {
      if (!name.endsWith('.download')) continue;
      const hash = name.slice(0, -'.download'.length);
      if (wantedHashes.has(hash)) continue;
      await this.fs.remove(this.fs.join(this.options.cacheDir, name)).catch(() => {});
      removed += 1;
    }
    return removed;
  }
}
